/** Device fingerprint: collect the components, hash them, send nothing else. */

import { sha256 } from "./hashing.js";
import { isMarketingAllowed } from "./consent.js";
import { debugLog } from "./state.js";
import {
  excludedComponents,
  serializeComponents,
  hasEnoughEntropy,
  type FingerprintComponents,
} from "./fingerprint-shape.js";

/**
 * A hash of what this device looks like, or nothing.
 *
 * WHAT THIS IS FOR. It fills exactly one gap: the same person, on the same
 * device, with no usable cookie. Incognito, cleared storage, a second browser
 * on one machine. It does NOT cross devices, by definition, and the e-mail
 * graph already answers that.
 *
 * THE GATE IS THE MARKETING CONSENT THAT ALREADY EXISTS, and it is checked
 * before anything is measured rather than after. Computing a fingerprint and
 * then discarding it because consent was missing is already the thing the rule
 * forbids: ePrivacy article 5(3) is about gaining access to information on the
 * terminal, not about what you do with it afterwards. That is also why
 * `tracking/index.ts` had to stop writing anything before the config arrives.
 *
 * ONLY THE HASH LEAVES. The components are more identifying and more sensitive
 * than the hash: a list of them describes a machine, the hash only matches it.
 *
 * AND IT IS THE WEAKEST CLASS OF EVIDENCE. Two identical phones with the same
 * OS in the same timezone produce the same value, so on the server side this
 * may attach a session to a person and nothing else: never create somebody,
 * never merge two, never promote a status. See identifier-classes.ts.
 */

let cachedFingerprint: string | null | undefined;

function screenComponent(): string | undefined {
  try {
    const s = window.screen;
    if (!s) return undefined;
    // Deliberately not window.innerWidth: that changes when the reader opens
    // the console or rotates a phone, and a component that changes within one
    // session is worse than a missing one.
    return [s.width, s.height, s.colorDepth, window.devicePixelRatio || 1].join("x");
  } catch {
    return undefined;
  }
}

function systemComponent(): string | undefined {
  try {
    const nav = window.navigator as Navigator & { platform?: string };
    return [nav.platform ?? "", nav.maxTouchPoints ?? 0].join("/");
  } catch {
    return undefined;
  }
}

function localesComponent(): string | undefined {
  try {
    const nav = window.navigator;
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
    return [nav.language ?? "", (nav.languages ?? []).join(","), zone].join("/");
  } catch {
    return undefined;
  }
}

function hardwareComponent(): string | undefined {
  try {
    const nav = window.navigator as Navigator & { deviceMemory?: number };
    return [nav.hardwareConcurrency ?? 0, nav.deviceMemory ?? 0].join("/");
  } catch {
    return undefined;
  }
}

function webglComponent(): string | undefined {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") as WebGLRenderingContext | null;
    if (!gl) return undefined;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    if (!info) return undefined;
    const renderer = gl.getParameter(info.UNMASKED_RENDERER_WEBGL) as string | null;
    const vendor = gl.getParameter(info.UNMASKED_VENDOR_WEBGL) as string | null;
    if (!renderer && !vendor) return undefined;
    return [vendor ?? "", renderer ?? ""].join("/");
  } catch {
    return undefined;
  }
}

function canvasComponent(): string | undefined {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 200;
    canvas.height = 40;
    const context = canvas.getContext("2d");
    if (!context) return undefined;
    context.textBaseline = "alphabetic";
    context.font = "14px 'Arial'";
    context.fillStyle = "#f60";
    context.fillRect(0, 0, 100, 20);
    context.fillStyle = "#069";
    context.fillText("The Social Tap \u{1F3AF}", 2, 20);
    return canvas.toDataURL().slice(-64);
  } catch {
    return undefined;
  }
}

function audioComponent(): string | undefined {
  try {
    const Ctor =
      (window as unknown as { OfflineAudioContext?: typeof OfflineAudioContext })
        .OfflineAudioContext ??
      (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext })
        .webkitOfflineAudioContext;
    if (!Ctor) return undefined;
    // The cheap variant: the properties of the context rather than a rendered
    // buffer. Rendering costs milliseconds on the main thread of somebody
    // else's site, and the reason we want audio at all is the hardware profile
    // it exposes, which these three already carry.
    const context = new Ctor(1, 1, 44100);
    return [context.sampleRate, context.destination.channelCount, context.length].join("/");
  } catch {
    return undefined;
  }
}

function isBrave(): boolean {
  try {
    return (
      (window.navigator as Navigator & { brave?: { isBrave?: unknown } }).brave !== undefined
    );
  } catch {
    return false;
  }
}

/**
 * The fingerprint for this page, computed at most once.
 *
 * Returns a promise because hashing is async in the browser (SubtleCrypto).
 * Resolves to null whenever we may not or cannot measure, and callers must
 * treat that as "no value" rather than as an empty string: an empty identifier
 * that every blocked visitor shares would be the worst possible match key.
 */
export function getFingerprint(): Promise<string | null> {
  if (cachedFingerprint !== undefined) return Promise.resolve(cachedFingerprint);

  // Before anything is measured, not after. See the note above.
  if (!isMarketingAllowed()) {
    debugLog("fingerprint", "skipped: no marketing consent");
    return Promise.resolve(null);
  }

  const components: FingerprintComponents = {
    screen: screenComponent(),
    system: systemComponent(),
    locales: localesComponent(),
    hardware: hardwareComponent(),
    webgl: webglComponent(),
    canvas: canvasComponent(),
    audio: audioComponent(),
  };
  const excluded = excludedComponents(window.navigator.userAgent ?? "", isBrave());

  if (!hasEnoughEntropy(components, excluded)) {
    cachedFingerprint = null;
    debugLog("fingerprint", "skipped: too few stable components");
    return Promise.resolve(null);
  }

  return sha256(serializeComponents(components, excluded)).then((hash) => {
    cachedFingerprint = hash ?? null;
    return cachedFingerprint;
  });
}
